import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import usePersonConnectionQuery from "../../../../hooks/query/connections/usePersonConnectionQuery";
import { Person } from "../../../../types/person";
import {
  PERSON_IMPLIES_CONNECTION_TYPE_LABELS,
  PersonConnection,
  SYSTEM_PERSON_IMPLIES_CONNECTION_TYPES,
  SystemPersonImpliesConnectionTypes,
} from "../../../../types/personConnection";

interface PersonImpliesConnectionsViewSectionProps {
  person: Person;
}

const formatImpliesType = (
  type: SystemPersonImpliesConnectionTypes | null
): string =>
  (type && PERSON_IMPLIES_CONNECTION_TYPE_LABELS[type]) || "N/A";

const getImpliesFields = (
  connection: PersonConnection
): ViewAccordionField[] => [
  {
    label: "Connection",
    value: (
      <Typography variant="body1">
        {formatImpliesType(
          (connection?.system_person_connection_type as
            | SystemPersonImpliesConnectionTypes
            | null) ?? null
        )}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof connection?.notes === "string" ? connection.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const PersonImpliesConnectionsViewSection: React.FC<
  PersonImpliesConnectionsViewSectionProps
> = ({ person }) => {
  const { useFetchConnectedEdges } = usePersonConnectionQuery();
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    person?._id,
    "inbound",
    {
      bodyParams: {
        system_person_connection_type: SYSTEM_PERSON_IMPLIES_CONNECTION_TYPES,
      },
    }
  );

  const items = useMemo<PersonConnection[]>(
    () => data?.data ?? [],
    [data]
  );

  return (
    <ConnectionAccordionSection<PersonConnection>
      title="Person Implies Connections"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(connection, index) =>
        connection?._key ?? `person-implies-${index}`
      }
      getItemTitle={(connection) =>
        connection?.fromDoc ? (
          <EntityReferenceComponent entity={connection.fromDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(connection) =>
        formatImpliesType(
          (connection?.system_person_connection_type as
            | SystemPersonImpliesConnectionTypes
            | null) ?? null
        )
      }
      getItemFields={(connection) => getImpliesFields(connection)}
      emptyText="No person implies connections"
    />
  );
};

export default PersonImpliesConnectionsViewSection;
